import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { useGetMany, useGetManyReference, useRecordContext, usePaginationState, } from 'react-admin';
import { UsingRegexp } from './constants';
/**
 * Hook that fetches records from another resource in a many to many scenario implemented with an associative resource.
 *
 * @example
 *
 * const { data, error, isFetching, isLoading, total } = useReferenceArrayFieldController({
 *      record: { id: 1, name: 'Eric Clapton' },
 *      reference: 'events',
 *      resource: 'artists',
 *      sort: { field: 'name', order: 'ASC' },
 *      source: 'id',
 *      through: 'performances',
 *      using: 'artist_id,event_id'
 * });
 *
 * @param {Object} options
 * @param {boolean} options.filter Filters to send with the request
 * @param {Object} options.record The current resource record
 * @param {string} options.reference The linked resource name
 * @param {string} options.resource The current resource name
 * @param {Object} options.sort The sort parameters
 * @param {string} options.sort.field The field used for sorting
 * @param {string} options.sort.order The sort order (asc, desc)
 * @param {string} options.source The key of the linked resource identifier
 * @param {string} options.through The name of the associative resource
 * @param {string} options.using A comma separated list of two field names which are the ids of the two linked resource in the associative resource
 * @param {boolean} options.unique Remove duplicates from the target table. defaults to true.
 *
 * @returns {UseReferenceManyToManyFieldValue} The reference props
 */
export var useReferenceManyToManyFieldController = function (props) {
    var filter = props.filter, reference = props.reference, sort = props.sort, _a = props.source, source = _a === void 0 ? 'id' : _a, through = props.through, using = props.using, _b = props.joinLimit, joinLimit = _b === void 0 ? 100 : _b, _c = props.perPage, perPage = _c === void 0 ? 25 : _c, _d = props.unique, unique = _d === void 0 ? true : _d;
    if (!using.match(UsingRegexp)) {
        throw new Error('useReferenceManyToManyFieldController incorrect `using` option format. `using` should be a string of two fields separated by a comma such as `book_id,author_id`');
    }
    var _e = using.match(UsingRegexp), sourceField = _e[1], targetField = _e[2];
    var record = useRecordContext(props);
    var throughManyReferences = useGetManyReference(through, {
        target: sourceField,
        id: get(record, source),
        sort: sort,
        filter: filter,
        pagination: { page: 1, perPage: joinLimit },
    });
    var allReferencesIds = throughManyReferences.data
        ? throughManyReferences.data
            .map(function (record) {
            return record[targetField];
        })
            .filter(function (id) { return !!id; })
        : [];
    var referencesIds = unique
        ? uniq(allReferencesIds)
        : allReferencesIds;
    var _f = usePaginationState({
        perPage: perPage,
        page: 1,
    }), page = _f.page, finalPerPage = _f.perPage, setPage = _f.setPage, setPerPage = _f.setPerPage;
    // Paginate the result client side
    var paginatedReferencesIds = referencesIds.slice((page - 1) * finalPerPage, page * finalPerPage);
    var references = useGetMany(reference, { ids: paginatedReferencesIds });
    return {
        resource: reference,
        data: references.data,
        sort: sort,
        error: throughManyReferences.error || references.error,
        isLoading: throughManyReferences.isLoading || references.isLoading,
        isFetching: throughManyReferences.isFetching || references.isFetching,
        total: referencesIds.length,
        hasNextPage: throughManyReferences.total != null
            ? page * finalPerPage < referencesIds.length
            : undefined,
        hasPreviousPage: page > 1,
        page: page,
        perPage: finalPerPage,
        setPage: setPage,
        setPerPage: setPerPage,
        refetch: function () {
            throughManyReferences.refetch();
            references.refetch();
        },
    };
};
